import type { Applicant, ApplicantIn, ApplicantPatch, PassportExtraction, Sex } from './api/types';
import { ddMmYyyyToIso, isoToDdMmYyyy } from './utils';

/** Editable applicant fields. Dates are yyyy-mm-dd for <input type="date">; '' means empty. */
export interface ApplicantForm {
  full_name: string;
  full_name_ar: string;
  date_of_birth: string;
  sex: Sex | '';
  place_of_birth: string;
  place_of_birth_ar: string;
  passport_type: string;
  /** Honorific, e.g. Ms. (printed before the English name). */
  title: string;
  /** Arabic honorific, e.g. السيدة (printed after the Arabic name). */
  title_ar: string;
}

export type ApplicantErrors = Partial<Record<keyof ApplicantForm, string>>;

export const EMPTY_APPLICANT: ApplicantForm = {
  full_name: '',
  full_name_ar: '',
  date_of_birth: '',
  sex: '',
  place_of_birth: '',
  place_of_birth_ar: '',
  passport_type: '',
  title: '',
  title_ar: '',
};

export function applicantToForm(a: Applicant | null | undefined): ApplicantForm {
  if (!a) return { ...EMPTY_APPLICANT };
  const sex = a.sex ?? '';
  // Saved before titles existed: show the default the report falls back to
  const title = a.title ? { title: a.title, title_ar: a.title_ar ?? '' } : defaultTitle(sex);
  return {
    full_name: a.full_name ?? '',
    full_name_ar: a.full_name_ar ?? '',
    date_of_birth: ddMmYyyyToIso(a.date_of_birth),
    sex,
    place_of_birth: a.place_of_birth ?? '',
    place_of_birth_ar: a.place_of_birth_ar ?? '',
    passport_type: a.passport_type ?? '',
    ...title,
  };
}

/** Fields of the passport scan that pre-fill the new-check form. */
export interface ExtractedPrefill {
  passport_no: string;
  passport_expiry: string;
  nationality_code: number | null;
  nationality_text: string;
  applicant: ApplicantForm;
  /** Form fields the scan couldn't read: highlight them for the user. */
  missing: string[];
}

export function prefillFromExtraction(x: PassportExtraction): ExtractedPrefill {
  const applicant: ApplicantForm = {
    full_name: x.full_name ?? '',
    full_name_ar: x.full_name_ar ?? '',
    date_of_birth: ddMmYyyyToIso(x.date_of_birth),
    sex: x.sex ?? '',
    place_of_birth: x.place_of_birth ?? '',
    place_of_birth_ar: x.place_of_birth_ar ?? '',
    passport_type: x.passport_type ?? '',
    ...defaultTitle(x.sex ?? ''),
  };
  const values: Record<string, unknown> = {
    passport_no: x.passport_no,
    passport_expiry: x.passport_expiry,
    nationality: x.nationality_code,
    full_name: x.full_name,
    full_name_ar: x.full_name_ar,
    date_of_birth: x.date_of_birth,
    sex: x.sex,
    place_of_birth: x.place_of_birth,
    place_of_birth_ar: x.place_of_birth_ar,
  };
  return {
    passport_no: x.passport_no ?? '',
    passport_expiry: ddMmYyyyToIso(x.passport_expiry),
    nationality_code: x.nationality_code,
    nationality_text: x.nationality_text ?? '',
    applicant,
    missing: Object.entries(values).filter(([, v]) => v === null || v === '').map(([k]) => k),
  };
}

const LATIN_NAME = /^[A-Za-z' -]+$/;
const TITLE = /^[A-Za-z][A-Za-z. ]*$/;
const ARABIC = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;

export function isBlank(form: ApplicantForm): boolean {
  // A title on its own is just the default that follows the sex
  return Object.entries(form).every(([k, v]) => k === 'title' || k === 'title_ar' || v.trim() === '');
}

/** Same rules as the API. `today` is yyyy-mm-dd (injectable for tests). */
export function validateApplicant(form: ApplicantForm, today: string = new Date().toISOString().slice(0, 10)): ApplicantErrors {
  const e: ApplicantErrors = {};
  if (isBlank(form)) return e;
  const name = form.full_name.trim();
  if (!name) e.full_name = 'The full name is required when you add applicant details.';
  else if (name.length < 2 || name.length > 128 || !LATIN_NAME.test(name)) e.full_name = '2–128 characters: letters, spaces, apostrophes and hyphens.';
  for (const k of ['full_name_ar', 'place_of_birth_ar'] as const) {
    const v = form[k].trim();
    if (v && (v.length > 128 || !ARABIC.test(v))) e[k] = 'Use Arabic letters (at most 128 characters).';
  }
  const pob = form.place_of_birth.trim();
  if (pob && (pob.length < 2 || pob.length > 64 || !LATIN_NAME.test(pob))) e.place_of_birth = '2–64 characters: letters, spaces, apostrophes and hyphens.';
  if (form.date_of_birth && form.date_of_birth >= today) e.date_of_birth = 'The date of birth must be in the past.';
  const pt = form.passport_type.trim();
  if (pt && !/^[A-Za-z]{1,2}$/.test(pt)) e.passport_type = '1–2 letters, e.g. P.';
  const t = form.title.trim();
  if (t && (t.length > 32 || !TITLE.test(t))) e.title = 'At most 32 characters: letters, spaces and dots, e.g. Mrs.';
  const tAr = form.title_ar.trim();
  if (tAr && (tAr.length > 32 || !ARABIC.test(tAr))) e.title_ar = 'Use Arabic letters (at most 32 characters).';
  return e;
}

function clean(form: ApplicantForm): Record<keyof ApplicantForm, string | null> {
  const up = (s: string) => s.trim().replace(/\s+/g, ' ').toUpperCase() || null;
  const trim = (s: string) => s.trim().replace(/\s+/g, ' ') || null;
  return {
    full_name: up(form.full_name),
    full_name_ar: trim(form.full_name_ar),
    date_of_birth: form.date_of_birth ? isoToDdMmYyyy(form.date_of_birth) : null,
    sex: form.sex || null,
    place_of_birth: up(form.place_of_birth),
    place_of_birth_ar: trim(form.place_of_birth_ar),
    passport_type: up(form.passport_type),
    title: trim(form.title),
    title_ar: trim(form.title_ar),
  };
}

/** Body for `applicant` on POST /checks: undefined when nothing was entered. */
export function toApplicantIn(form: ApplicantForm): ApplicantIn | undefined {
  if (isBlank(form)) return undefined;
  const c = clean(form);
  const out: ApplicantIn = { full_name: c.full_name ?? '' };
  for (const [k, v] of Object.entries(c)) {
    if (k !== 'full_name' && v !== null) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/** Body for PATCH /checks/{id}/applicant: only the fields that changed (null clears one). */
export function toApplicantPatch(form: ApplicantForm, original: Applicant | null): ApplicantPatch {
  const before = clean(applicantToForm(original));
  const after = clean(form);
  const patch: Record<string, unknown> = {};
  for (const k of Object.keys(after) as (keyof ApplicantForm)[]) {
    if (after[k] !== before[k]) patch[k] = after[k];
  }
  return patch as ApplicantPatch;
}

export const SEX_LABEL: Record<Sex, string> = { M: 'Male', F: 'Female', X: 'Unspecified' };

/**
 * The Arabic place of birth follows the English one: what to do after a form
 * change. 'respell' = ask for a new Arabic spelling of `next.place_of_birth`,
 * 'clear' = the English was emptied, null = leave the Arabic alone (English
 * unchanged, not a valid name yet, or both changed together, e.g. a scan prefill).
 */
export function placeArabicAction(prev: ApplicantForm, next: ApplicantForm): 'respell' | 'clear' | null {
  return arabicFollowAction(prev, next, 'place_of_birth', 'place_of_birth_ar');
}

/** An English field and the Arabic field that can follow it. */
export type ArabicPair = ['full_name', 'full_name_ar'] | ['place_of_birth', 'place_of_birth_ar'];

/** placeArabicAction for any English/Arabic pair of fields. */
export function arabicFollowAction(
  prev: ApplicantForm,
  next: ApplicantForm,
  ...[en, ar]: ArabicPair
): 'respell' | 'clear' | null {
  const before = prev[en].trim().toUpperCase();
  const after = next[en].trim().toUpperCase();
  if (before === after || prev[ar] !== next[ar]) return null;
  if (!after) return next[ar] ? 'clear' : null;
  return after.length >= 2 && LATIN_NAME.test(after) ? 'respell' : null;
}

// ---------------------------------------------------------------------------
// Titles: "Ms. NAME" in English, "NAME السيدة" in Arabic
// ---------------------------------------------------------------------------

/** Titles offered in the form, with the Arabic for a man and for a woman. */
export const TITLES: { en: string; ar: { M: string; F: string } }[] = [
  { en: 'Mr.', ar: { M: 'السيد', F: 'السيد' } },
  { en: 'Ms.', ar: { M: 'السيدة', F: 'السيدة' } },
  { en: 'Mrs.', ar: { M: 'السيدة', F: 'السيدة' } },
  { en: 'Miss', ar: { M: 'الآنسة', F: 'الآنسة' } },
  { en: 'Dr.', ar: { M: 'الدكتور', F: 'الدكتورة' } },
  { en: 'Eng.', ar: { M: 'المهندس', F: 'المهندسة' } },
  { en: 'Prof.', ar: { M: 'البروفيسور', F: 'البروفيسورة' } },
  { en: 'Capt.', ar: { M: 'الكابتن', F: 'الكابتن' } },
  { en: 'Sheikh', ar: { M: 'الشيخ', F: 'الشيخ' } },
  { en: 'Sheikha', ar: { M: 'الشيخة', F: 'الشيخة' } },
];

/** The preset matching `title` (case and trailing dot ignored), if any. */
export function findTitle(title: string): (typeof TITLES)[number] | undefined {
  const key = title.trim().replace(/\.$/, '').toLowerCase();
  return key ? TITLES.find((t) => t.en.replace(/\.$/, '').toLowerCase() === key) : undefined;
}

/** Arabic for a preset title ('' for a custom one). */
export function titleArabic(title: string, sex: Sex | ''): string {
  const preset = findTitle(title);
  return preset ? preset.ar[sex === 'F' ? 'F' : 'M'] : '';
}

/** Mr. for a man, Ms. for a woman, none otherwise. */
export function defaultTitle(sex: Sex | ''): Pick<ApplicantForm, 'title' | 'title_ar'> {
  const title = sex === 'M' ? 'Mr.' : sex === 'F' ? 'Ms.' : '';
  return { title, title_ar: titleArabic(title, sex) };
}

/**
 * The sex changed: keep the title in step unless the user chose their own.
 * A title still at the old default becomes the new default; a preset like Dr.
 * keeps its English and gets the Arabic for the new sex (if its Arabic wasn't
 * edited by hand).
 */
export function withSex(form: ApplicantForm, sex: Sex | ''): ApplicantForm {
  const next = { ...form, sex };
  const old = defaultTitle(form.sex);
  if (!form.title.trim() || (form.title === old.title && form.title_ar === old.title_ar)) {
    return { ...next, ...defaultTitle(sex) };
  }
  if (form.title_ar === titleArabic(form.title, form.sex)) {
    return { ...next, title_ar: titleArabic(form.title, sex) || form.title_ar };
  }
  return next;
}

/** A new English title: its Arabic follows when it's a preset. */
export function withTitle(form: ApplicantForm, title: string): ApplicantForm {
  const arabic = titleArabic(title, form.sex);
  const arabicWasAuto = !form.title_ar || form.title_ar === titleArabic(form.title, form.sex);
  return { ...form, title, title_ar: arabic || (arabicWasAuto ? '' : form.title_ar) };
}

/** "Ms. BONTU GIRMA" — what the report prints. */
export function titledName(name: string, title: string): string {
  return [title.trim(), name.trim()].filter(Boolean).join(' ');
}

/** "بنت جرم السيدة" — Arabic puts the title after the name. */
export function titledNameAr(name: string, title: string): string {
  return [name.trim(), title.trim()].filter(Boolean).join(' ');
}
